import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll for navbar
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        message: formData.message
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d5eg092llb01bjs85e10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img src="https://public.readdy.ai/ai/img_res/b1c71d7e-80dd-41ac-9ec5-cabd627c9fa8.png" alt="SIN RIVAL Logo" className="h-12 w-auto" />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('vpn-app')} className="text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer whitespace-nowrap">VPN Application</button>
              <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer whitespace-nowrap">Features</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer whitespace-nowrap">Contact</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer whitespace-nowrap">About Us</button>
            </div>

            <div className="hidden lg:block">
              <button onClick={() => scrollToSection('contact')} className="bg-black text-white px-6 py-3 rounded-full font-medium hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap flex items-center">
                Get Started
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>

            {/* Mobile menu button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-gray-700 cursor-pointer">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-8 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer">Home</button>
              <button onClick={() => scrollToSection('vpn-app')} className="block w-full text-left text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer">VPN Application</button>
              <button onClick={() => scrollToSection('features')} className="block w-full text-left text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer">Features</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer">Contact</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-700 hover:text-gray-900 font-medium py-2 cursor-pointer">About Us</button>
              <button onClick={() => scrollToSection('contact')} className="bg-black text-white px-6 py-3 rounded-full font-medium w-full mt-4 cursor-pointer whitespace-nowrap">Get Started</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1F36] via-[#2D3142] to-[#6C5CE7]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        {/* Floating shapes */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-40 right-20 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-purple-400/10 rounded-full blur-2xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-16 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <div className="inline-block bg-purple-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <span className="text-white text-sm font-medium">SECURE VPN SOLUTION</span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
                Protect Your Digital Privacy
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Experience unlimited, secure browsing with military-grade encryption
              </p>
              <div className="flex flex-wrap gap-4">
                <button onClick={() => scrollToSection('vpn-app')} className="bg-black text-white px-8 py-4 rounded-full font-medium hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap">
                  Download Now
                </button>
                <button onClick={() => scrollToSection('features')} className="border-2 border-white text-white px-8 py-4 rounded-full font-medium hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap">
                  Learn More
                </button>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="relative z-10">
                <div className="w-full h-96 flex items-center justify-center">
                  <img src="https://public.readdy.ai/ai/img_res/8d96234339ac804cc8a8270357655e17.jpg" alt="VPN Application Interface" className="w-full h-full object-contain drop-shadow-2xl" />
                </div>
              </div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Application Section */}
      <section id="vpn-app" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">Our VPN Application</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A powerful VPN solution designed to keep your online activities private and secure
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left - Screenshot */}
              <div className="relative h-96 lg:h-auto">
                <img src="https://readdy.ai/api/search-image?query=VPN%20application%20dashboard%20interface%20screenshot%20showing%20connection%20toggle%20button%2C%20server%20location%20selector%20with%20world%20map%2C%20connection%20speed%20meter%2C%20data%20usage%20statistics%2C%20security%20status%20indicators%2C%20modern%20clean%20UI%20with%20purple%20and%20blue%20accent%20colors%2C%20dark%20mode%20interface%2C%20professional%20software%20design%2C%20detailed%20app%20screen%20capture&width=800&height=900&seq=vpn-dashboard-screen&orientation=portrait" alt="VPN Dashboard" className="w-full h-full object-cover object-top" />
              </div>

              {/* Right - Features */}
              <div className="p-12 lg:p-16 flex flex-col justify-center">
                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex-shrink-0">
                      <i className="ri-shield-check-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Military-Grade Encryption</h3>
                      <p className="text-gray-600">AES-256 encryption protects your data from hackers and surveillance</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex-shrink-0">
                      <i className="ri-global-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Global Server Network</h3>
                      <p className="text-gray-600">Connect to servers worldwide for unrestricted access to content</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex-shrink-0">
                      <i className="ri-flashlight-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Lightning Fast Speed</h3>
                      <p className="text-gray-600">Optimized servers ensure fast connections without compromising security</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-500 to-green-500 rounded-xl flex-shrink-0">
                      <i className="ri-lock-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">No-Log Policy</h3>
                      <p className="text-gray-600">We never track, collect, or share your private data</p>
                    </div>
                  </div>
                </div>

                <button onClick={() => scrollToSection('contact')} className="mt-12 bg-black text-white px-8 py-4 rounded-full font-medium hover:bg-gray-800 transition-all inline-flex items-center justify-center cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line mr-2"></i>
                  Download Application
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">Why Choose SIN RIVAL VPN</h2>
            <p className="text-xl text-gray-600">Advanced security features for complete peace of mind</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-500 rounded-full mb-6 mx-auto">
                <i className="ri-shield-star-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Advanced Security</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Bank-level encryption and secure protocols protect your data from cyber threats and ensure complete privacy online
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full mb-6 mx-auto">
                <i className="ri-speed-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Ultra-Fast Connection</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Experience blazing-fast speeds with our optimized server infrastructure designed for seamless streaming and browsing
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full mb-6 mx-auto">
                <i className="ri-eye-off-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Complete Privacy</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Strict no-logs policy ensures your browsing history and personal information remain completely private and secure
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-teal-500 to-green-500 rounded-full mb-6 mx-auto">
                <i className="ri-earth-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Global Access</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Access content from anywhere in the world with our extensive network of servers across multiple countries
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-500 rounded-full mb-6 mx-auto">
                <i className="ri-device-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Multi-Device Support</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Protect all your devices with one account - compatible with Windows, Mac, iOS, Android, and more
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full mb-6 mx-auto">
                <i className="ri-customer-service-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">24/7 Support</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Our dedicated support team is always available to help you with any questions or technical issues
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-[#1A1F36]">
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left - Image */}
            <div className="lg:col-span-2">
              <div className="relative h-96 lg:h-[500px] w-full">
                <img src="https://readdy.ai/api/search-image?query=Modern%20technology%20company%20office%20workspace%20with%20professional%20team%20collaborating%20on%20cybersecurity%20and%20VPN%20development%2C%20warm%20ambient%20lighting%2C%20contemporary%20interior%20design%2C%20computer%20screens%20showing%20network%20security%20diagrams%2C%20professional%20business%20environment%2C%20high-tech%20atmosphere%2C%20clean%20and%20organized%20space%2C%20teamwork%20and%20innovation%20concept&width=600&height=700&seq=about-office&orientation=portrait" alt="SIN RIVAL Team" className="w-full h-full object-cover rounded-2xl" />
              </div>
            </div>

            {/* Right - Content */}
            <div className="lg:col-span-3">
              <div className="inline-block bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <span className="text-blue-300 text-sm font-medium">ABOUT SIN RIVAL</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Dedicated to Your Online Security
              </h2>
              <div className="space-y-4 text-gray-300 text-lg leading-relaxed mb-8">
                <p>
                  SIN RIVAL S.R.L. is a technology company committed to protecting your digital privacy and security. We develop innovative applications that empower users to browse the internet safely and freely.
                </p>
                <p>
                  Our VPN application is built with cutting-edge encryption technology and a user-first approach. We believe that everyone deserves access to a secure and private internet experience, without compromising on speed or usability.
                </p>
                <p>
                  Based in Argentina, we serve users worldwide with our reliable and efficient VPN solution. Our mission is to make online privacy accessible to everyone through simple, powerful technology.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-blue-400 text-xl"></i>
                  </div>
                  <span className="text-white/90">12 DE OCTUBRE 99, Santiago del Estero, G4300GQP, Argentina</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-mail-line text-blue-400 text-xl"></i>
                  </div>
                  <a href="mailto:contact@sinrivalsrl.com" className="text-white/90 hover:text-white transition-colors cursor-pointer">contact@sinrivalsrl.com</a>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-phone-line text-blue-400 text-xl"></i>
                  </div>
                  <a href="tel:+18156495884" className="text-white/90 hover:text-white transition-colors cursor-pointer">+1 8156495884</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">Get In Touch</h2>
            <p className="text-xl text-gray-600">Have questions? We're here to help</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-base"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-base"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all resize-none text-base"
                      placeholder="Your message (max 500 characters)"
                    />
                    <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-black text-white py-4 rounded-full font-medium hover:bg-gray-800 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-lg"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                      Thank you! Your message has been sent successfully.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                      Sorry, there was an error sending your message. Please try again.
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-xl mb-4">
                  <i className="ri-mail-line text-purple-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Email</h3>
                <a href="mailto:contact@sinrivalsrl.com" className="text-gray-600 hover:text-purple-600 transition-colors cursor-pointer">contact@sinrivalsrl.com</a>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-xl mb-4">
                  <i className="ri-phone-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Phone</h3>
                <a href="tel:+18156495884" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">+1 8156495884</a>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl mb-4">
                  <i className="ri-map-pin-line text-teal-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Address</h3>
                <p className="text-gray-600">12 DE OCTUBRE 99<br />Santiago del Estero<br />G4300GQP, Argentina</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2D3142] text-white py-16 relative overflow-hidden">
        {/* World map pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M10 10h80v80H10z\' fill=\'none\' stroke=\'%23fff\' stroke-width=\'0.5\'/%3E%3C/svg%3E")', backgroundSize: '100px 100px' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-8 lg:px-16 relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-12 mb-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <img src="https://public.readdy.ai/ai/img_res/b1c71d7e-80dd-41ac-9ec5-cabd627c9fa8.png" alt="SIN RIVAL Logo" className="h-10 w-auto mb-4" />
              <p className="text-gray-400 mb-6">Your trusted VPN solution provider</p>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-bold text-lg mb-4">Product</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('vpn-app')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">VPN Application</button></li>
                <li><button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">Features</button></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">About Us</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">Contact</button></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-bold text-lg mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-bold text-lg mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>contact@sinrivalsrl.com</li>
                <li>+1 8156495884</li>
                <li>Santiago del Estero, Argentina</li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">© 2025 SIN RIVAL S.R.L. All rights reserved</p>
              
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-white hover:bg-white/10 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-white hover:bg-white/10 transition-all cursor-pointer">
                  <i className="ri-twitter-x-line text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-white hover:bg-white/10 transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              </div>

              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
